import { useQueries } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  Select,
  StatTile,
  Tabs,
  type Column,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { useBranch } from '@/features/branch/branch-context';
import { withoutDemoClock } from '@/features/registers/demo-clock';
import {
  useRegisterKinds,
  useRegisterRecords,
  type RegisterAction,
  type RegisterKind,
  type RegisterRecord,
} from '@/features/registers/api';
import { api } from '@/lib/api';
import { useUrlState } from '@/lib/url-state';
import { PageContainer } from '@/layouts/PageContainer';
import { klDate, todayKl } from '@/pages/calendar/time';
import {
  COMPLAINT_KIND,
  isAppComplaint,
  matchesSource,
  SOURCE_OPTIONS,
  type ComplaintSource,
} from './complaint-format';
import { AppSourceBadge, DeadlineCell } from './ComplaintParts';
import styles from './OperationalRecords.module.css';
import { RecordCreateDrawer, RecordDrawer } from './RecordDrawer';
import { dayLabel, formatValue, shownStatus } from './register-format';
import { actionsFor, useStaffNames } from './register-hooks';

/** Records needing attention: overdue, or open in a warning/danger status. */
function attention(rows: RegisterRecord[] | undefined) {
  return (rows ?? []).filter((r) => {
    const tone = shownStatus(r).tone;
    return (
      !r.closedAt && (r.overdue || tone === 'warning' || tone === 'danger')
    );
  }).length;
}

/**
 * Register area page (prototype hub() + lgCard()): a tab per register kind,
 * each a card with its rule, "Add" action and records; rows open a drawer
 * with details, timeline and the actions the role may take.
 */
export function OperationalRecords({
  title,
  kinds,
  icon,
  tiles,
  before,
}: {
  title: string;
  kinds: string[];
  icon?: ReactNode;
  /** Status tiles above a single register (prototype complaints). */
  tiles?: boolean;
  /** Context cards shown above the register (e.g. rules). */
  before?: ReactNode;
}) {
  const { t } = useTranslation();
  const { selected } = useBranch();
  const catalog = useRegisterKinds();
  const available = useMemo(
    () =>
      kinds
        .map((k) => catalog.data?.find((c) => c.kind === k))
        .filter((k): k is RegisterKind => !!k),
    [catalog.data, kinds],
  );
  const [tab, setTab] = useUrlState('tab', '');
  const current = available.find((k) => k.kind === tab) ?? available[0];
  // Counts for the tab bar come from every kind's records.
  const all = useQueries({
    queries: available.map((k) => ({
      queryKey: ['register-records', k.kind, selected?.id ?? 'all'],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        api
          .get<RegisterRecord[]>(`/registers/${k.kind}`, { signal })
          .then((r) => r.data.map((x) => withoutDemoClock(x))),
      enabled: !(k.branchScoped && !selected),
    })),
  });

  if (catalog.isLoading)
    return (
      <PageContainer>
        {before}
        <LoadingState />
      </PageContainer>
    );
  if (catalog.error)
    return (
      <PageContainer>
        {before}
        <ErrorState
          error={catalog.error}
          onRetry={() => void catalog.refetch()}
        />
      </PageContainer>
    );
  if (!current)
    return (
      <PageContainer>
        {before}
        <Card title={title}>
          <EmptyState icon={icon} title={t('operations.noRecords')} />
        </Card>
      </PageContainer>
    );
  return (
    <PageContainer>
      <div className={styles.stack}>
        {available.length > 1 && (
          <Tabs
            variant="bar"
            label={title}
            value={current.kind}
            onChange={setTab}
            items={available.map((k, i) => ({
              value: k.kind,
              label: k.label,
              count: attention(all[i]?.data),
            }))}
          />
        )}
        {before}
        <RegisterCard
          key={current.kind}
          kind={current}
          icon={icon}
          tiles={tiles}
        />
      </div>
    </PageContainer>
  );
}

export function RegisterCard({
  kind: k,
  icon,
  tiles,
}: {
  kind: RegisterKind;
  icon?: ReactNode;
  tiles?: boolean;
}) {
  const { t } = useTranslation();
  const { hasPermission } = useSession();
  // RL-1826: who archives (data_security:manage) can list the archived
  // records of a register and bring one back.
  const canSeeArchive = hasPermission('data_security:manage');
  const [archivedView, setArchivedView] = useState(false);
  const archived = canSeeArchive && archivedView;
  const q = useRegisterRecords(k, archived);
  const staff = useStaffNames();
  const [open, setOpen] = useState<{
    id: string;
    action?: RegisterAction;
  } | null>(null);
  const [adding, setAdding] = useState(false);
  // RL-1137: complaints show their source (app or clinic), owner and deadlines.
  const complaints = k.kind === COMPLAINT_KIND;
  const [source, setSource] = useState<ComplaintSource>('all');
  const rows = q.data;
  const shown = complaints
    ? rows?.filter((r) => matchesSource(r, source))
    : rows;
  const showPatient = k.patient !== 'none';
  const columnFields = k.fields.filter((f) => f.column);
  const showDue = complaints || !!rows?.some((r) => r.dueAt);
  // RL-1667 (gap #135): a register whose own fields already show the owner
  // (record and report requests, compliance licences) gets one Owner column.
  const ownerField = columnFields.some((f) => f.key === 'owner');
  const showOwner = !ownerField && (complaints || !!rows?.some((r) => r.owner));
  const columns: Column<RegisterRecord>[] = [
    {
      key: 'ref',
      header: 'Ref',
      render: (r) => (
        <>
          {/* RL-1667 (gap #138): an unnumbered register (message templates) shows the date only, not "-". */}
          {r.number && <span className={styles.ref}>{r.number}</span>}
          <span className={styles.sub}>{dayLabel(r.createdAt)}</span>
          {complaints && isAppComplaint(r) && (
            <span className={styles.refBadge}>
              <AppSourceBadge r={r} />
            </span>
          )}
        </>
      ),
    },
    ...(showPatient
      ? [
          {
            key: 'patient',
            header: 'Patient',
            render: (r: RegisterRecord) => r.patient?.fullName ?? '-',
          },
        ]
      : []),
    ...(columnFields.length
      ? columnFields.map((f, i) => ({
          key: `f-${f.key}`,
          header: f.label,
          render: (r: RegisterRecord) =>
            i === 0 ? (
              <strong>{formatValue(f, r.data[f.key], staff.names)}</strong>
            ) : (
              formatValue(f, r.data[f.key], staff.names)
            ),
        }))
      : [
          {
            key: 'title',
            header: 'What',
            render: (r: RegisterRecord) => <strong>{r.title}</strong>,
          },
        ]),
    ...(showOwner
      ? [
          {
            key: 'owner',
            header: 'Owner',
            render: (r: RegisterRecord) =>
              r.owner?.fullName ?? (complaints ? 'Unassigned' : '-'),
          },
        ]
      : []),
    ...(showDue
      ? [
          {
            key: 'due',
            header: complaints ? 'Next deadline' : 'Due',
            render: (r: RegisterRecord) =>
              complaints ? (
                <DeadlineCell r={r} />
              ) : r.dueAt ? (
                <span className={r.overdue ? styles.late : undefined}>
                  {dayLabel(r.dueAt)}
                  {r.overdue ? ' · late' : ''}
                </span>
              ) : (
                '-'
              ),
          },
        ]
      : []),
    {
      key: 'status',
      header: 'Status',
      render: (r) => {
        // RL-1823: licences show Expires soon / Expired from their expiry.
        const st = shownStatus(r);
        return (
          <>
            <Badge tone={st.tone} dot={!r.closedAt}>
              {st.label}
            </Badge>
            {r.archivedAt && (
              <>
                {' '}
                <Badge>Archived</Badge>
              </>
            )}
          </>
        );
      },
    },
    {
      key: 'acts',
      header: <span className="visually-hidden">Actions</span>,
      align: 'right',
      render: (r) => {
        const primary = actionsFor(k, r).find((a) => a.primary);
        return (
          <span className={styles.acts}>
            {primary && (
              <Button
                size="sm"
                variant={primary.danger ? 'danger' : 'primary'}
                onClick={() => setOpen({ id: r.id, action: primary })}
              >
                {primary.label}
              </Button>
            )}
            <Button size="sm" onClick={() => setOpen({ id: r.id })}>
              Open
            </Button>
          </span>
        );
      },
    },
  ];
  const month = todayKl().slice(0, 7);
  return (
    <>
      {tiles && (
        <div className={styles.stats}>
          <StatTile
            highlight
            label="Open"
            value={rows ? rows.filter((r) => !r.closedAt).length : '—'}
            caption={k.statuses
              .filter((s) => s.value !== 'closed')
              .slice(0, 3)
              .map((s) => s.label.toLowerCase())
              .join(', ')}
          />
          <StatTile
            label={
              k.statuses.find((s) => s.value === k.initialStatus)?.label ??
              'New'
            }
            value={
              rows
                ? rows.filter((r) => r.status === k.initialStatus).length
                : '—'
            }
            caption="Not yet picked up"
          />
          <StatTile
            label="Late"
            value={rows ? rows.filter((r) => r.overdue).length : '—'}
            caption="Past the due date"
          />
          <StatTile
            label="This month"
            value={
              rows
                ? rows.filter((r) => klDate(r.createdAt).startsWith(month))
                    .length
                : '—'
            }
            caption="Recorded since the 1st"
          />
          {complaints && (
            <StatTile
              label="From the app"
              value={
                rows
                  ? rows.filter((r) => !r.closedAt && isAppComplaint(r)).length
                  : '—'
              }
              caption="Open, made in the patient app"
            />
          )}
        </div>
      )}
      <Card
        title={k.label}
        flush
        actions={
          <>
            {complaints && (
              <div className={styles.filters}>
                <Field label="Source">
                  <Select
                    value={source}
                    options={SOURCE_OPTIONS}
                    onChange={(e) =>
                      setSource(e.target.value as ComplaintSource)
                    }
                  />
                </Field>
              </div>
            )}
            {k.rule && <span className={styles.crumb}>{k.rule}</span>}
            {/* RL-1828 (gap #150): say why there is no "Add" (e.g. the clinic manager). */}
            {!k.canWrite && <Badge tone="neutral">View only</Badge>}
            {canSeeArchive && (
              <label className={styles.crumb}>
                <input
                  type="checkbox"
                  checked={archivedView}
                  onChange={(e) => setArchivedView(e.target.checked)}
                />{' '}
                Archived only
              </label>
            )}
            {k.canWrite && !archived && (
              <Button
                size="sm"
                variant="primary"
                icon={<Plus size={16} />}
                onClick={() => setAdding(true)}
              >
                {`Add ${k.noun}`}
              </Button>
            )}
          </>
        }
      >
        <DataTable
          caption={k.label}
          rows={shown}
          rowKey={(r) => r.id}
          loading={q.isLoading}
          error={q.error}
          onRetry={() => void q.refetch()}
          columns={columns}
          empty={
            <EmptyState
              icon={icon}
              title={
                q.needsBranch
                  ? t('operations.chooseBranch')
                  : archived
                    ? 'Nothing archived in this register'
                    : complaints && source !== 'all' && rows?.length
                      ? 'No complaints from this source'
                      : t('operations.noRecords')
              }
              body={
                !q.needsBranch && k.canWrite && !archived
                  ? `Use "Add ${k.noun}" to record the first one.`
                  : undefined
              }
            />
          }
        />
      </Card>
      {open && (
        <RecordDrawer
          kind={k}
          id={open.id}
          initialAction={open.action}
          staff={staff}
          onClose={() => setOpen(null)}
        />
      )}
      {adding && (
        <RecordCreateDrawer
          kind={k}
          staff={staff}
          onClose={() => setAdding(false)}
          onCreated={(id) => {
            setAdding(false);
            setOpen({ id });
          }}
        />
      )}
    </>
  );
}
